import {
  CursorClickIcon,
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  ImageIcon,
  ImageSquareIcon,
  LockSimpleIcon,
  PlusIcon,
  SquareIcon,
  TextTIcon,
} from '@phosphor-icons/react';
import * as React from 'react';

import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ICON_BUTTON, ROW_HOVER, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export type LayerKind = 'text' | 'shape' | 'image' | 'logo' | 'button';

export interface WorkbenchLayer {
  id: string;
  name: string;
  kind: LayerKind;
  hidden?: boolean;
  /** Locked rows cannot be reordered or hidden — today only the background. */
  locked?: boolean;
}

export interface LayerAddItem {
  id: string;
  label: string;
  /** One line under the label — the ChatGPT `+`-menu shape the founder specified. */
  description: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** Starts a new group above this item. */
  separatorBefore?: boolean;
}

export interface LayersPanelProps {
  /** Already in paint order — index 0 is the TOP of the z-stack. */
  layers: readonly WorkbenchLayer[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  onToggleVisibility?: (id: string, hidden: boolean) => void;
  /** Called with source and destination indices after a drag or ⌥↑/⌥↓. */
  onReorder?: (fromIndex: number, toIndex: number) => void;
  /**
   * Remove a layer. Reached with Delete/Backspace on the focused row — see the
   * keyboard handler for why there is no per-row button. Locked layers never
   * call this.
   */
  onDelete?: (id: string) => void;
  addItems?: readonly LayerAddItem[];
  onAdd?: (itemId: string) => void;
  className?: string;
}

const KIND_ICON: Record<LayerKind, React.ComponentType<{ className?: string }>> = {
  text: TextTIcon,
  shape: SquareIcon,
  image: ImageIcon,
  logo: ImageSquareIcon,
  button: CursorClickIcon,
};

/**
 * The layers list — the biggest structural gap between Mora's canvas and every
 * design tool a marketer has used.
 *
 * Reorder affordance is drag-first with an ⌥↑/⌥↓ keyboard path, and there are
 * deliberately NO per-row arrow buttons: four buttons × N rows in a ~280px
 * column is exactly the "competing rectangles" failure the whole recipe exists
 * to avoid. Celtra's own reviewers report drowning in per-layer chrome at
 * scale; the cheapest time to avoid that is before the first row ships.
 *
 * WHY (measured, Hightouch): rows 28px, radius 7px, 4% accent tint on the
 * selected row with the label AND icon going accent-coloured. Mora uses 32px —
 * 28px is under the comfortable row floor and off the 4px grid.
 */
export function LayersPanel({
  layers,
  selectedId,
  onSelect,
  onToggleVisibility,
  onReorder,
  onDelete,
  addItems,
  onAdd,
  className,
}: LayersPanelProps): JSX.Element {
  /**
   * When this panel is inside a dialog-based shell (the workbench mounts as a
   * workspace `Inspector`, i.e. a raw `DialogPrimitive.Content`), the add-menu
   * must portal INSIDE that content. Portalled to `document.body` it is a DOM
   * sibling, and dismissing it reads to Radix as a click outside the dialog,
   * which closes the whole surface. The hook returns null outside any dialog,
   * where the default body portal is correct.
   */
  const menuContainer = useDialogContentContainer();
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);
  const [overIndex, setOverIndex] = React.useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (!onReorder) return;
    if (to < 0 || to >= layers.length || to === from) return;
    if (layers[from]?.locked || layers[to]?.locked) return;
    onReorder(from, to);
  };

  return (
    <section className={cn('flex min-h-0 flex-col', className)}>
      {/* MEASURED alignment, not a guess. The `Layers` label and the inspector's
          first section label ("Size variants") are the two things a user sees at
          the top of the two rails, and a mismatch between them is the first
          thing the eye finds.

          `pt-6` was the right answer while the inspector wrapped its strip in an
          extra `p-3`, giving that column a 22px top inset — off the 4px grid,
          and still 2px away (75 vs 73). That double padding is gone; the
          inspector now uses the same 10px inset as every section below it, so
          this matches at `pt-2.5` instead. Measured after: both labels at y=61.

          `px-4` aligns what the eye actually compares: the label's glyphs against
          the rows' type icons, not the label against an invisible row box. The
          row box starts 8px in (the list's `px-2`) and its content another 8px
          inside that (the row's own `px-2`), which is the inset the hover fill
          needs; measured, the label sat at x=57 while the first type icon sat at
          x=65. `px-4` puts both at 65, and puts the `+` on the same line as each
          row's trailing visibility icon. */}
      <header className="flex items-center justify-between gap-2 px-4 pb-2 pt-2.5">
        <span className={SECTION_LABEL}>Layers</span>
        {addItems && addItems.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="Add a layer"
              className={cn(
                ICON_BUTTON,
                'inline-flex size-5 items-center justify-center rounded-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <PlusIcon className="size-3.5" />
            </DropdownMenuTrigger>
            {/* `align="start"`, NOT `"end"` — this trigger lives in the LEFT rail.
                With `align="end"` the menu's RIGHT edge anchors to the trigger's
                right edge, so a 288px menu off a trigger whose right edge is at
                x≈256 resolves to left = -32 and gets clipped to the viewport edge:
                measured `translate(-49px, 82px)`, rect `{x: 0, y: 83, w: 288}`,
                overlapping the layer list with every row label truncated
                mid-word. It reproduced in both themes and made every add-layer
                action — Text, Shape, Brand lockup — look broken on sight.
                The header's `…` menu uses `align="end"` correctly because that
                trigger sits near the RIGHT edge; copying its props here was the
                mistake. Anchor the left edge instead and the menu opens rightward
                into the canvas, which is where there is room. */}
            <DropdownMenuContent align="start" className="max-w-72" container={menuContainer}>
              {addItems.map((item) => (
                <React.Fragment key={item.id}>
                  {item.separatorBefore ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem
                    disabled={item.disabled}
                    onSelect={() => onAdd?.(item.id)}
                    className="items-start gap-2 rounded-sm px-2 py-1.5"
                  >
                    <span className="mt-0.5 text-muted-foreground">{item.icon}</span>
                    <span className="min-w-0">
                      <span className="block text-caption text-foreground">{item.label}</span>
                      <span className="block text-detail text-muted-foreground">
                        {item.description}
                      </span>
                    </span>
                  </DropdownMenuItem>
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </header>

      <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-2">
        {layers.map((layer, index) => {
          const Icon = KIND_ICON[layer.kind];
          const isSelected = layer.id === selectedId;
          const isDropTarget = overIndex === index && dragIndex !== null && dragIndex !== index;

          return (
            <li key={layer.id}>
              <div
                role="option"
                tabIndex={0}
                aria-selected={isSelected}
                aria-current={isSelected || undefined}
                draggable={Boolean(onReorder) && !layer.locked}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(event) => {
                  if (dragIndex === null) return;
                  event.preventDefault();
                  setOverIndex(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragIndex !== null) move(dragIndex, index);
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setOverIndex(null);
                }}
                onClick={() => onSelect(layer.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(layer.id);
                    return;
                  }
                  /**
                   * DELETE IS KEYBOARD-ONLY, deliberately. `deleteLayer` has
                   * existed in the store since the workbench shipped and had NO
                   * reachable UI — you could add layers forever and never remove
                   * one. A per-row trash icon is the obvious fix and is exactly
                   * what this component's own doctrine (above) rules out: four
                   * buttons × N rows is the "competing rectangles" failure the
                   * recipe exists to avoid. So it follows the ⌥↑/⌥↓ precedent
                   * already established here, and the footer hint teaches it —
                   * the same way that hint already teaches restacking.
                   * Locked layers (the Background) refuse, matching `move()`.
                   */
                  if (event.key === 'Delete' || event.key === 'Backspace') {
                    if (!onDelete || layer.locked) return;
                    event.preventDefault();
                    onDelete(layer.id);
                    return;
                  }
                  if (!event.altKey) return;
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    move(index, event.shiftKey ? 0 : index - 1);
                  }
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    move(index, event.shiftKey ? layers.length - 1 : index + 1);
                  }
                }}
                className={cn(
                  'group flex h-8 cursor-default items-center gap-2 rounded-sm px-2',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isSelected
                    ? 'bg-selection-accent/10 text-selection-accent'
                    : cn('text-foreground/85', ROW_HOVER),
                  isDropTarget && 'ring-1 ring-ring/60',
                  layer.hidden && !isSelected && 'text-muted-foreground',
                )}
              >
                {/* TYPE ICON FIRST, flush to the row's own padding. The handle
                    used to lead the row, and because it is hover-only it left a
                    blank 14px + 8px gap spacer in front of every layer: the type
                    icon started 22px from the row edge while the section header's
                    label started at 8px, so the list read as if it were indented
                    for no reason. Founder: "it makes the text feel unaligned."
                    Measured: icon 22px → 8px from the row edge, agreeing with the
                    header label. The reference does the same — icon, then label,
                    no leading gutter. */}
                <Icon className="size-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-caption">{layer.name}</span>

                {/* The handle is now a trailing affordance next to visibility. It
                    is a hint, not the grab target — the ROW carries `draggable`,
                    so a drag started anywhere on the row works and the handle only
                    advertises that. `cursor-grab` says so without promising a
                    smaller target than the row actually offers.

                    FULL `text-muted-foreground`, NOT `/50`. Founder, on the dark
                    rail: "the drag handles aren't visible". Measured in sRGB
                    against each theme's `--background`: at `/50` the revealed
                    handle sits at **2.71:1 in dark and 1.96:1 in light** — under
                    the 3:1 floor WCAG 1.4.11 sets for a UI component, i.e. it was
                    failing in BOTH themes and dark only made it obvious. `/70`
                    fixes dark (4.19) and still fails light (2.71). Dropping the
                    alpha entirely gives **7.42:1 dark / 4.72:1 light**, and the
                    handle still reads one step below the layer name beside it
                    (`text-foreground/85`), which is the hierarchy the hint wants.
                    The hover-reveal is unchanged — the point was that the
                    revealed state has to actually read. */}
                {onReorder && !layer.locked ? (
                  <DotsSixVerticalIcon
                    aria-hidden
                    className="size-3.5 shrink-0 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                  />
                ) : null}

                {layer.locked ? (
                  <LockSimpleIcon aria-hidden className="size-3.5 shrink-0 opacity-50" />
                ) : onToggleVisibility ? (
                  <button
                    type="button"
                    aria-label={layer.hidden ? `Show ${layer.name}` : `Hide ${layer.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleVisibility(layer.id, !layer.hidden);
                    }}
                    className={cn(
                      ICON_BUTTON,
                      'inline-flex size-6 shrink-0 items-center justify-center rounded-sm transition-opacity',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      // A hidden layer must advertise itself permanently, or the
                      // user hunts for a headline that is simply switched off.
                      layer.hidden ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                    )}
                  >
                    {layer.hidden ? (
                      <EyeSlashIcon className="size-3.5" />
                    ) : (
                      <EyeIcon className="size-3.5" />
                    )}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
